import React, { useEffect, useState } from "react";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { getAllThesisGrades } from "../../services/feedbackService";
import axiosInstance from "../../api/axios";
import {
  Box,
  Tooltip,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import "./datatable.scss";

export const StudentGrades = () => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedInfo, setExpandedInfo] = useState({ rowId: null, examiner: null });

  // --- Modal editor state ---
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorRow, setEditorRow] = useState(null);
  const [editorField, setEditorField] = useState(null);
  const [editorValue, setEditorValue] = useState("");
  const apiRef = useGridApiRef();

  const THESIS_STATUS_LABELS = {
    REPORT_REVISION_REQUIRED: "Viva Cleared, Revise Report",
    RESUBMISSION: "Viva Not Cleared",
    PHASE_COMPLETED: "Viva Cleared",
  };

  const THESIS_STATUS_OPTIONS = [
    { value: "REPORT_REVISION_REQUIRED", label: "Viva Cleared, Revise Report" },
    { value: "RESUBMISSION", label: "Viva Not Cleared" },
    { value: "PHASE_COMPLETED", label: "Viva Cleared" },
  ];

  // --- Fetch data ---
  const fetchThesisGrades = async () => {
    try {
      const data = await getAllThesisGrades();
      setGrades(data.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThesisGrades();
  }, []);

  // --- Toggle expanded feedback ---
  const toggleRowExpansion = (rowId, examiner) => {
    if (expandedInfo.rowId === rowId && expandedInfo.examiner === examiner) {
      setExpandedInfo({ rowId: null, examiner: null });
    } else {
      setExpandedInfo({ rowId, examiner });
    }
  };

  // --- Open / close modal editors ---
  const openEditor = (row, field) => {
    setEditorRow(row);
    setEditorField(field);
    setEditorValue(row[field] ?? "");
    setEditorOpen(true);
  };
  const closeEditor = () => {
    setEditorOpen(false);
    setEditorRow(null);
    setEditorField(null);
    setEditorValue("");
  };

  // --- Save edited value ---
  const saveEditor = async () => {
  if (!editorRow || !editorField) return;

  const id = editorRow.thesisStatusId;
  const thesisFeedbackId1 = editorRow.thesisFeedbackId1;
  const thesisFeedbackId2 = editorRow.thesisFeedbackId2;
  
  //console.log(editorRow);
  const studentId = editorRow.studentId;

  // helper: convert empty string -> null
  const normalize = (v) =>
    v === undefined || v === null
      ? null
      : typeof v === "string" && v.trim() === ""
      ? null
      : v;

  // build payload using current editorValue for the edited field
  const payload = {
    thesisStatusId: id,
    studentId: studentId,
    finalMarks:
      editorField === "finalMarks"
        ? normalize(editorValue)
        : normalize(editorRow.finalMarks),
    finalGrade:
      editorField === "finalGrade"
        ? normalize(editorValue)
        : normalize(editorRow.finalGrade),
    finalFeedback:
      editorField === "finalFeedback"
        ? normalize(editorValue)
        : normalize(editorRow.finalFeedback),
    termThesisStatus:
      editorField === "termThesisStatus"
        ? normalize(editorValue)
        : normalize(editorRow.termThesisStatus),
  };

 // console.log("Outgoing payload →", JSON.stringify(payload, null, 2));
 // console.log("Axios instance:", axiosInstance.defaults.transformRequest);


  try {
    const res = await axiosInstance.put(
      "/updateFinalGradeMarksFeedbackStatus",
      payload,
      { headers: { "Content-Type": "application/json" },
      transformRequest: [(data) => JSON.stringify(data)], }
    );

    // merge server result into grid
    setGrades((prev) =>
      prev.map((g) =>
        g.thesisStatusId === id ? { ...g, ...payload } : g
      )
    );

    closeEditor();
  } catch (err) {
    console.error("Save failed:", err);
    alert("Save failed");
  }
};



  // --- Share feedback button ---
  const shareFeedback = async (row) => {
    try {
      const { thesisStatusId, thesisFeedbackId1, thesisFeedbackId2 } = row;
      await axiosInstance.put(`/shareThesisGrade?id=${thesisStatusId}`);
      if (thesisFeedbackId1) {
       await axiosInstance.put(`/shareThesisFeedback?id=${thesisFeedbackId1}`);
      }
      if(thesisFeedbackId2) {
       await axiosInstance.put(`/shareThesisFeedback?id=${thesisFeedbackId2}`);
      }
      alert("Grade shared successfully!");
      setGrades((prev) =>
        prev.map((g) =>
          g.thesisStatusId === thesisStatusId
            ? { ...g, share_to_student: true }
            : g
        )
      );
    } catch (err) {
      console.error("Error sharing grade:", err);
      alert("Failed to share grade.");
    }
  };

  // --- Columns ---
  const columns = [
    { field: "studentName", headerName: "Student", width: 170 },
    { field: "enrollmentNumber", headerName: "Enrollment No", width: 130 },
    { field: "reportType", headerName: "Type", width: 100 },
    { field: "termType", headerName: "Submission Type", width: 150 },
    { field: "examinerName1", headerName: "Examiner 1", width: 150 },
    {
        field: "totalMarks1",
        headerName: "Total Marks by Examiner 1",
        width: 200,
        renderCell: (params) => {
          const row = params.row;
          const isExpanded =
            expandedInfo.rowId === row.thesisStatusId && expandedInfo.examiner === 1;
          return (
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <span>{row.totalMarks1 ?? "-"}</span>
            <button
              onClick={() => toggleRowExpansion(row.thesisStatusId, 1)}
              style={{
                backgroundColor: "#1976d2",
                color: "white",
                border: "none",
                borderRadius: "6px",
                padding: "4px 8px",
                cursor: "pointer",
              }}
            >
              {isExpanded ? "Hide" : "Show"}
            </button>
            </Box>
          );
        },
      },
      { field: 'gradeOverallByExaminer1', headerName: 'Grade', width: 120 },
    { field: "examinerName2", headerName: "Examiner 2", width: 150 },
    {
        field: "totalMarks2",
        headerName: "Total Marks by Examiner 2",
        width: 200,
        renderCell: (params) => {
          const row = params.row;
          const isExpanded =
            expandedInfo.rowId === row.thesisStatusId && expandedInfo.examiner === 2;
          return (
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <span>{row.totalMarks2 ?? "-"}</span>
            <button
              onClick={() => toggleRowExpansion(row.thesisStatusId, 2)}
              style={{
                backgroundColor: "#1976d2",
                color: "white",
                border: "none",
                borderRadius: "6px",
                padding: "4px 8px",
                cursor: "pointer",
              }}
            >
              {isExpanded ? "Hide" : "Show"}
            </button>
            </Box>
          );
        },
      },
      { field: 'gradeOverallByExaminer2', headerName: 'Grade', width: 100 },

    {
      field: "finalMarks",
      headerName: "Final Marks",
      width: 100,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <span>{params.value ?? "-"}</span>
          <Tooltip title="Edit marks">
            <IconButton
              size="small"
              onClick={() => openEditor(params.row, "finalMarks")}
              disabled={params.row.share_to_student === true}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },

    {
      field: "finalGrade",
      headerName: "Final Grade",
      width: 100,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <span>{params.value ?? "-"}</span>
          <Tooltip title="Edit grade">
            <IconButton
              size="small"
              onClick={() => openEditor(params.row, "finalGrade")}
              disabled={params.row.share_to_student === true}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },

    {
      field: "finalFeedback",
      headerName: "Final Feedback",
      width: 200,
      renderCell: (params) => {
        const row = params.row;
        const isExpanded =
          expandedInfo.rowId === row.thesisStatusId &&
          expandedInfo.examiner === "final";
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "140px",
              }}
            >
              {row.finalFeedback
                ? row.finalFeedback.length > 20
                  ? row.finalFeedback.slice(0, 20) + "..."
                  : row.finalFeedback
                : "-"}
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <Tooltip title="Edit feedback">
                <IconButton
                  size="small"
                  onClick={() => openEditor(row, "finalFeedback")}
                  disabled={params.row.share_to_student === true}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <button
                onClick={() => toggleRowExpansion(row.thesisStatusId, "final")}
                // disabled={params.row.share_to_student === true}
                style={{
                  backgroundColor: isExpanded ? "#d32f2f" : "#1976d2",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "3px 8px",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                }}
              >
                {isExpanded ? "Hide" : "Show"}
              </button>
            </div>
          </Box>
        );
      },
    },

    {
      field: "thesisVivaStatus",
      headerName: "Status",
      width: 250,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <span>
            {THESIS_STATUS_LABELS[params.value] ?? params.value ?? "-"}
          </span>
          <Tooltip title="Edit Status">
            <IconButton
              size="small"
              onClick={() => openEditor(params.row, "thesisVivaStatus")}
              disabled={params.row.share_to_student === true}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    }

  ];

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      width: 180,
      renderCell: (params) => {
        const { share_to_student } = params.row;
        return (
          <div className="cellAction">
            <button
              onClick={() => shareFeedback(params.row)}
              disabled={share_to_student === true}
            >
              {share_to_student ? "Shared" : "Share"}
            </button>
          </div>
        );
      },
    },
  ];

  if (loading) return <p>Loading…</p>;
  if (error) return <p>Error: {error.message}</p>;

  return (
    <div className="datatable">
      <DataGrid
        apiRef={apiRef}
        rows={grades}
        getRowId={(row) => row.thesisStatusId}
        columns={columns.concat(actionColumn)}
        pageSizeOptions={[5, 10]}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 10 } },
        }}
      />

      <Collapse in={!!expandedInfo.rowId} timeout="auto" unmountOnExit>
            {expandedInfo.rowId && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#f9f9f9",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "0.95rem",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                }}
              >
                {(() => {
                  const selectedRow = grades.find(
                    (g) => g.thesisStatusId === expandedInfo.rowId
                  );
                  if (!selectedRow) return null;
      
                  const examiner = expandedInfo.examiner;
                  let prefix = null;
                  let title = "";
      
                  if (examiner === 1) {
                    prefix = "ByExaminer1";
                    title = "Feedback — Examiner 1";
                  } else if (examiner === 2) {
                    prefix = "ByExaminer2";
                    title = "Feedback — Examiner 2";
                  } else if (examiner === "final") {
                    title = "Final Feedback";
                  }
      
                  return (
                    <>
                  <h4 style={{ marginTop: 0 }}>{title}</h4>
      
                  {examiner === "final" ? (
                    <>
                      <p>
                         {selectedRow.finalFeedback ?? "-"}
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        <strong>Report Marks:</strong> {selectedRow[`reportMarks${prefix}`] ?? 0}
                      </p>
                      <p>
                        <strong>Presentation Marks:</strong> {selectedRow[`presentationMarks${prefix}`] ?? 0}
                      </p>
                      <p>
                        <strong>Viva Marks:</strong> {selectedRow[`vivaMarks${prefix}`] ?? 0}
                      </p>
                      <p>
                        <strong>Report Feedback:</strong> {selectedRow[`reportFeedback${prefix}`] ?? "-"}
                      </p>
                      <p>
                        <strong>Presentation Feedback:</strong> {selectedRow[`presentationFeedback${prefix}`] ?? "-"}
                      </p>
                      <p>
                        <strong>Other Feedback:</strong> {selectedRow[`otherFeedback${prefix}`] ?? "-"}
                      </p>
                      <p>
                        <strong>Overall Feedback:</strong> {selectedRow[`overallFeedback${prefix}`] ?? "-"}
                      </p>
                    </>
                  )}
                </>
      
                  );
                })()}
              </Box>
            )}
          </Collapse>

      {/* ---------- Modal Editor ---------- */}
      <Dialog open={editorOpen} onClose={closeEditor} fullWidth maxWidth="sm">
        <DialogTitle>
          {editorField === "finalGrade"
            ? `Edit Final Grade — ${editorRow?.studentName ?? ""}`
            : editorField === "finalMarks"
            ? `Edit Final Marks — ${editorRow?.studentName ?? ""}`
            : editorField === "finalFeedback"
            ?  `Edit Final Feedback — ${editorRow?.studentName ?? ""}`
            : `Edit Status — ${editorRow?.studentName ?? ""}`}
        </DialogTitle>
        <DialogContent>
          {editorField === "termThesisStatus" ? (
            <TextField
              select
              fullWidth
              label="Select Status"
              value={editorValue || ""}
              onChange={(e) => setEditorValue(e.target.value)}
              SelectProps={{ native: true }}
              autoFocus
            >
              <option value="" disabled>Select Status</option>
              {THESIS_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </TextField>
          ):
           editorField === "finalFeedback" ? (
            <TextField
              fullWidth
              multiline
              minRows={6}
              label="Final Feedback (Ctrl+Enter to save)"
              value={editorValue}
              onChange={(e) => setEditorValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.ctrlKey && e.key === "Enter") {
                  e.preventDefault();
                  saveEditor();
                }
              }}
              autoFocus
            />
          ) : (
            <TextField
              fullWidth
              label="Enter value (leave empty to clear)"
              value={editorValue ?? ""}
              onChange={(e) => setEditorValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  saveEditor();
                }
              }}
              autoFocus
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor}>Cancel</Button>
          <Button variant="contained" onClick={saveEditor}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
